import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  endpoints,
  type CampaignResponse,
  type CampaignChannelDraft,
  type CreateCampaignRequest,
} from "@/lib/api";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { ChannelPicker } from "@/components/ChannelPicker";
import { Input } from "@/components/ui/Input";
import { Lbl } from "@/components/ui/Lbl";
import { Select } from "@/components/ui/Select";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/Table";

// ---------------------------------------------------------------------------
// 投放管理 (Campaigns) — v1.2: campaigns is the umbrella; each KOL deal lives
// in campaign_channels. The list view shows the umbrella + KOL count +
// deliverable count; per-channel detail lives on /campaigns/:id.
// ---------------------------------------------------------------------------

const STATUSES = [
  { value: "", label: "全部(不含归档)" },
  { value: "planned", label: "计划中" },
  { value: "active", label: "进行中" },
  { value: "completed", label: "已完成" },
  { value: "cancelled", label: "已取消" },
  { value: "archived", label: "已归档" },
];

const STATUS_VARIANT: Record<string, "success" | "info" | "secondary" | "danger" | "warning"> = {
  planned: "info",
  active: "success",
  completed: "secondary",
  cancelled: "danger",
  archived: "secondary",
};

const STATUS_LABEL: Record<string, string> = {
  planned: "计划中",
  active: "进行中",
  completed: "已完成",
  cancelled: "已取消",
  archived: "已归档",
};

const PAGE_SIZE = 50;

export default function Campaigns() {
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);

  const listQ = useQuery({
    queryKey: ["campaigns", { status, page }],
    queryFn: () => endpoints.campaigns({ status, page, limit: PAGE_SIZE }),
  });

  // v2.1.3 — default view hides archived. Backend filter doesn't yet support
  // "exclude archived"; doing it client-side is fine because campaigns are
  // a small set (< 100 typically). Specific status filter (status='archived')
  // still works to surface them.
  const rawRows = listQ.data?.data ?? [];
  const rows = status === "" ? rawRows.filter((r) => r.status !== "archived") : rawRows;
  const hasNext = listQ.data?.next_page !== undefined;

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border bg-background px-6 py-3">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">投放管理</h1>
          <span className="text-sm text-muted-foreground">
            第 {page} 页 · 本页 {rows.length} 条
          </span>
          <Button className="ml-auto" size="sm" onClick={() => setShowCreate(true)}>
            + 新建投放
          </Button>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="w-40"
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </Select>
          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1 || listQ.isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              上一页
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasNext || listQ.isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              下一页
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-background">
        {listQ.isLoading && <p className="p-4 text-sm text-muted-foreground">加载中…</p>}
        {listQ.isError && (
          <p className="p-4 text-sm text-destructive">
            加载失败：{(listQ.error as Error).message}
          </p>
        )}
        {listQ.data && rows.length === 0 && (
          <p className="p-4 text-sm text-muted-foreground">
            还没有投放记录 — 点右上角"新建投放"开始。一次投放下面可以挂多个 KOL。
          </p>
        )}
        {rows.length > 0 && (
          <Table>
            <THead>
              <TR>
                <TH>名称</TH>
                <TH>状态</TH>
                <TH className="text-right">KOL 数</TH>
                <TH className="text-right">交付内容</TH>
                <TH>起止</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((c) => (
                <CampaignRow key={c.id} c={c} />
              ))}
            </TBody>
          </Table>
        )}
      </div>

      {showCreate && (
        <CreateCampaignModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.refetch();
          }}
        />
      )}
    </div>
  );
}

function CampaignRow({ c }: { c: CampaignResponse }) {
  return (
    <TR>
      <TD>
        <Link
          to={`/campaigns/${c.id}`}
          className="font-medium hover:underline"
        >
          {c.name}
        </Link>
        <div className="text-xs text-muted-foreground">#{c.id}</div>
      </TD>
      <TD>
        <Badge variant={STATUS_VARIANT[c.status] ?? "secondary"}>{STATUS_LABEL[c.status] ?? c.status}</Badge>
      </TD>
      <TD className="text-right tabular-nums">
        {c.channel_count > 0 ? c.channel_count : <span className="text-muted-foreground">0</span>}
      </TD>
      <TD className="text-right tabular-nums">
        {c.deliverable_count > 0 ? (
          c.deliverable_count
        ) : (
          <span className="text-muted-foreground">0</span>
        )}
      </TD>
      <TD className="text-xs text-muted-foreground">
        {c.start_date ?? "—"} → {c.end_date ?? "—"}
      </TD>
    </TR>
  );
}

// -----------------------------------------------------------------------------
// New-campaign modal — umbrella metadata + N rows of campaign_channels.
// -----------------------------------------------------------------------------

interface ChannelDraftRow extends CampaignChannelDraft {
  _key: number; // stable React key independent of channel_id changes
}

function newDraftRow(): ChannelDraftRow {
  return {
    _key: Math.random(),
    channel_id: 0,
    referral_code: "",
    kol_uid: "",
    payment_currency: "USD",
  };
}

function CreateCampaignModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (created: CampaignResponse) => void;
}) {
  const qc = useQueryClient();
  const [umbrella, setUmbrella] = useState({
    name: "",
    status: "planned",
    start_date: "",
    end_date: "",
    notes: "",
  });
  const [drafts, setDrafts] = useState<ChannelDraftRow[]>([newDraftRow()]);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: CreateCampaignRequest) => endpoints.createCampaign(body),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      onCreated(created);
    },
    onError: (err) => setError((err as Error).message),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!umbrella.name.trim()) return setError("请填写投放名称");
    const validDrafts = drafts.filter((d) => d.channel_id > 0);
    if (validDrafts.length === 0) return setError("至少需要 1 个 KOL");

    const body: CreateCampaignRequest = {
      name: umbrella.name.trim(),
      status: umbrella.status,
      start_date: umbrella.start_date || undefined,
      end_date: umbrella.end_date || undefined,
      notes: umbrella.notes || undefined,
      channels: validDrafts.map(({ _key: _, ...rest }) => ({
        ...rest,
        referral_code: rest.referral_code || undefined,
        kol_uid: rest.kol_uid || undefined,
        agreed_deliverables: rest.agreed_deliverables || undefined,
        payment_currency: rest.payment_currency || undefined,
        notes: rest.notes || undefined,
        deal_amount: rest.deal_amount ? Number(rest.deal_amount) : undefined,
      })),
    };
    mutation.mutate(body);
  }

  function updateDraft(key: number, patch: Partial<ChannelDraftRow>) {
    setDrafts((rows) => rows.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  }

  // v2.1.9 — pickChannelForDraft mirrors the per-channel auto-fill in
  // AddCampaignChannelModal: when operator selects a KOL, fetch the
  // channel and pre-fill referral_code/kol_uid from channels-level data.
  // Only writes to empty draft fields so user edits aren't clobbered.
  function pickChannelForDraft(key: number, id: number | null) {
    updateDraft(key, { channel_id: id ?? 0 });
    if (!id) return;
    endpoints.channel(id).then((ch) => {
      setDrafts((rows) =>
        rows.map((r) => {
          if (r._key !== key) return r;
          const patch: Partial<ChannelDraftRow> = {};
          if (!r.referral_code && ch.ReferralCode) patch.referral_code = ch.ReferralCode;
          if (!r.kol_uid && ch.KolUid) patch.kol_uid = ch.KolUid;
          return { ...r, ...patch };
        }),
      );
    }).catch(() => {});
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <form
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-border bg-background p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <h2 className="mb-1 text-lg font-semibold">新建投放</h2>
        <p className="mb-4 text-xs text-muted-foreground">
          一次投放（活动）下面可以挂多个 KOL，每个 KOL 用不同的返佣码 / UID / 金额。
        </p>

        {/* Umbrella fields */}
        <fieldset className="mb-4 grid grid-cols-2 gap-3 rounded-md border border-border bg-muted/20 p-3">
          <legend className="px-2 text-xs font-semibold text-muted-foreground">
            活动信息
          </legend>
          <Lbl label="名称 *" full>
            <Input
              value={umbrella.name}
              onChange={(e) => setUmbrella({ ...umbrella, name: e.target.value })}
              placeholder="eg. 2026 Q2 US launch"
              autoFocus
            />
          </Lbl>
          <Lbl label="状态">
            <Select
              value={umbrella.status}
              onChange={(e) => setUmbrella({ ...umbrella, status: e.target.value })}
            >
              <option value="planned">planned</option>
              <option value="active">active</option>
              <option value="completed">completed</option>
              <option value="cancelled">cancelled</option>
            </Select>
          </Lbl>
          <Lbl label="开始日期">
            <Input
              type="date"
              value={umbrella.start_date}
              onChange={(e) => setUmbrella({ ...umbrella, start_date: e.target.value })}
            />
          </Lbl>
          <Lbl label="结束日期">
            <Input
              type="date"
              value={umbrella.end_date}
              onChange={(e) => setUmbrella({ ...umbrella, end_date: e.target.value })}
            />
          </Lbl>
          <Lbl label="备注" full>
            <textarea
              className="h-16 w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={umbrella.notes}
              onChange={(e) => setUmbrella({ ...umbrella, notes: e.target.value })}
            />
          </Lbl>
        </fieldset>

        {/* Per-KOL deals */}
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold">KOL 合约 ({drafts.length})</h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setDrafts((r) => [...r, newDraftRow()])}
          >
            + 添加 KOL
          </Button>
        </div>

        <div className="space-y-3">
          {drafts.map((d, idx) => (
            <fieldset
              key={d._key}
              className="grid grid-cols-2 gap-3 rounded-md border border-border bg-background p-3"
            >
              <legend className="px-2 text-xs font-semibold text-muted-foreground">
                KOL #{idx + 1}
              </legend>
              <Lbl label="频道 *" full>
                <ChannelPicker
                  value={d.channel_id || null}
                  onChange={(id) => pickChannelForDraft(d._key, id ?? null)}
                />
              </Lbl>
              <Lbl label="返佣码">
                <Input
                  value={d.referral_code ?? ""}
                  onChange={(e) => updateDraft(d._key, { referral_code: e.target.value })}
                  placeholder="eg. 6JXUEK"
                />
              </Lbl>
              <Lbl label="KOL 在 Bitbase 的 UID">
                <Input
                  value={d.kol_uid ?? ""}
                  onChange={(e) => updateDraft(d._key, { kol_uid: e.target.value })}
                  placeholder="eg. 12345678"
                />
              </Lbl>
              <Lbl label="金额">
                <Input
                  type="number"
                  step="0.01"
                  value={d.deal_amount ?? ""}
                  onChange={(e) =>
                    updateDraft(d._key, {
                      deal_amount: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                  placeholder="服务端 AES-GCM 加密"
                />
              </Lbl>
              <Lbl label="币种">
                <Input
                  value={d.payment_currency ?? ""}
                  onChange={(e) => updateDraft(d._key, { payment_currency: e.target.value })}
                  placeholder="USD / USDT / CNY"
                />
              </Lbl>
              <Lbl label="约定交付物" full>
                <Input
                  value={d.agreed_deliverables ?? ""}
                  onChange={(e) =>
                    updateDraft(d._key, { agreed_deliverables: e.target.value })
                  }
                  placeholder="eg. 1 主内容 + 2 短内容 + 顶置 7 天"
                />
              </Lbl>
              <Lbl label="备注" full>
                <Input
                  value={d.notes ?? ""}
                  onChange={(e) => updateDraft(d._key, { notes: e.target.value })}
                />
              </Lbl>
              {drafts.length > 1 && (
                <div className="col-span-2 flex justify-end">
                  <button
                    type="button"
                    className="text-xs text-destructive hover:underline"
                    onClick={() =>
                      setDrafts((r) => r.filter((row) => row._key !== d._key))
                    }
                  >
                    移除此 KOL
                  </button>
                </div>
              )}
            </fieldset>
          ))}
        </div>

        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            取消
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? "创建中…" : "创建投放"}
          </Button>
        </div>
        <p className="mt-3 text-[10px] text-muted-foreground">
          创建后每个 KOL 的频道 lifecycle 会被切换到 <code>active</code>；返佣码会被下一轮
          sponsorship_detection 自动识别。
        </p>
      </form>
    </div>
  );
}
